import { useMemo, useState } from "react";
import FileUpload from "../../components/common/FileUpload";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import SubHeadings from "../../components/SubHeadings";
import { useEditMode } from "../../context/EditModeContext";
import { QueryItem } from "../../types/Query";
import { useFormEdit } from "../../util/hooks/useFormEdit";
import OtherData from "./OtherData";
import QueryTimeLine from "./QueryTimeLine";
import QueryApis from "../../api/QueryApis";
import { popupNavigation } from "../../util/functions/UniversalFunctions";
import { useNavigate } from "react-router-dom";
import { useQueryForm } from "../../util/hooks/useQueryForm";
import SubButton from "../../components/SubButtons";
import { useBranchCode } from "../../util/hooks/useBranch";
import AssignEnquiryModal from "../../components/modal/AssignEnquiryModel";

export default function ProfileData({
  data,
  onUpdated,
}: {
  data: QueryItem;
  onUpdated: (data: QueryItem) => void;
}) {
  const { formData, setFormData } = useFormEdit<QueryItem>(data);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const branchCode = useBranchCode();

  const { formFields } = useQueryForm(branchCode);

  const otherDetailFields = useMemo(
    () => formFields.filter((f) => f.parentId >= 11),
    [formFields],
  );

  const { isEditing, toggleEdit } = useEditMode();
  const [showAssign, setShowAssign] = useState(false);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;
    const newValue = type === "radio" ? value : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.id) return;

    try {
      setSaving(true);
      const res = await QueryApis.updateQuery(data.id, formData);

      if (res?.status === 1) {
        onUpdated(res.data);
        toggleEdit();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row w-full">
      <form
        onSubmit={handleSubmit}
        className="bg-white py-5 w-full px-4 md:pl-9 flex flex-col gap-7"
      >
        <div className="flex justify-between items-center">
          <SubHeadings text="Profile Information" />
          <div className="flex justify-end items-end gap-2 px-7">
            {isEditing && (
              <SubButton text="Save" color="bg-success-600" type="submit" />
            )}
            <SubButton
              padding="px-4"
              text={isEditing ? "Cancel" : "Edit"}
              color={isEditing ? "bg-error-500" : "bg-warning-600"}
              onClick={toggleEdit}
            />
          </div>
        </div>
        <div className="flex flex-col-reverse md:flex-row gap-4 lg:gap-9">
          <div className="grid gap-7 w-full">
            <div className="grid gap-7">
              <CustomInput
                name="studentName"
                label="Student name"
                readOnly={!isEditing}
                type="text"
                placeholder="Enter student name"
                value={formData.studentName || ""}
                onChange={handleInputChange}
                inputClassName="w-full"
              />

              <CustomSelect
                name=""
                label="Assign to"
                readOnly={!isEditing}
                value={formData?.assignedCounsellorName || ""}
                options={[]}
                openAsPopup
                onClick={() => {
                  setShowAssign(true);
                }}
                placeholder="Assign To"
                onChange={handleInputChange}
                selectClassName={
                  data.assignedCounsellorName
                    ? "text-primary-600 font-semibold"
                    : ""
                }
              />

              <div className="grid lg:grid-cols-2 gap-7">
                <CustomSelect
                  name="status"
                  label="Status"
                  readOnly={!isEditing}
                  value={formData.status || ""}
                  options={[]}
                  openAsPopup
                  onClick={() =>
                    navigate(popupNavigation("queriesStatus"), {
                      state: { queryId: formData.id },
                    })
                  }
                  placeholder="Select"
                  onChange={handleInputChange}
                  optionClassName="text-success-600 font-semibold"
                />

                <CustomInput
                  name="dateOfBirth"
                  label="Date of birth"
                  type="date"
                  readOnly={!isEditing}
                  value={formData.dateOfBirth || ""}
                  onChange={handleInputChange}
                  inputClassName="w-full"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center w-full">
              <div className="flex gap-7">
                <label className="text-sm font-semibold text-neutral-400 block">
                  Gender
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <CustomInput
                      name="gender"
                      type="radio"
                      checked={formData.gender === "MALE"}
                      value="MALE"
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      inputClassName="w-3 h-3"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        formData.gender === "MALE"
                          ? "text-primary-500"
                          : "text-neutral-300"
                      }`}
                    >
                      Male
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <CustomInput
                      name="gender"
                      type="radio"
                      checked={formData.gender === "FEMALE"}
                      value="FEMALE"
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      inputClassName="w-3 h-3"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        formData.gender === "FEMALE"
                          ? "text-primary-500"
                          : "text-neutral-300"
                      }`}
                    >
                      Female
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:pl-7 pt-7 md:py-7">
            <FileUpload
              onUploadComplete={(url) =>
                setFormData((prev) => ({ ...prev, profileImage: url }))
              }
              folder="indigo_cab"
              className="md:w-52 w-36 aspect-square"
              disabled={!isEditing}
            />
          </div>
        </div>
        <OtherData
          fields={otherDetailFields}
          formData={formData}
          setFormData={setFormData}
          isEditing={isEditing}
        />
      </form>
      <QueryTimeLine data={data} />

      {showAssign && data.id && (
        <AssignEnquiryModal
          queryId={data.id}
          onClose={() => setShowAssign(false)}
          onAssigned={() => {
            setShowAssign(false);
            onUpdated({ ...data });
          }}
        />
      )}
    </div>
  );
}
